/**
 * Изменения утверждённого бюджета (БМ-10): допработы, экономия, перерасход, перенос — только со ссылкой на
 * документ-основание. Предлагает тот, кто правит бюджет; решает тот, кто его утверждает, но не автор.
 */
import {
  BUDGET_CHANGE_KINDS,
  type BudgetChangeBasisOptions,
  type BudgetChangeInput,
  type BudgetChangeKind,
  type BudgetChangesView,
} from '@epc/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { tengeShort } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

export const budgetChangesQuery = (projectId: string, versionId?: string) =>
  queryOptions({
    queryKey: ['budget', 'changes', projectId, versionId ?? null],
    queryFn: () => api<BudgetChangesView>(`/projects/${projectId}/budget-changes`, { query: { versionId } }),
  });

const BTN = 'min-h-[44px] md:min-h-0';
const STATUS = {
  proposed: { title: 'ждёт решения', tone: 'warning' },
  approved: { title: 'утверждено', tone: 'success' },
  rejected: { title: 'отклонено', tone: 'neutral' },
} as const;

type BasisType = 'changeId' | 'amendmentId' | 'contractId';
const BASIS_TITLES: Record<BasisType, string> = {
  changeId: 'Запрос на изменение',
  amendmentId: 'Допсоглашение',
  contractId: 'Договор',
};

function ProposeDialog({
  projectId,
  versionId,
  sections,
  onClose,
}: {
  projectId: string;
  versionId: string;
  sections: Array<{ id: string; name: string }>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const basis = useQuery({
    queryKey: ['budget', 'changes-basis', projectId],
    queryFn: () => api<BudgetChangeBasisOptions>(`/projects/${projectId}/budget-changes/basis`),
  });
  const [kind, setKind] = useState<BudgetChangeKind>('extra_work');
  const [sectionLineId, setSectionLineId] = useState(sections[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [basisType, setBasisType] = useState<BasisType>('changeId');
  const [basisId, setBasisId] = useState('');
  const options =
    basisType === 'changeId'
      ? (basis.data?.changes ?? [])
      : basisType === 'amendmentId'
        ? (basis.data?.amendments ?? [])
        : (basis.data?.contracts ?? []);
  // Экономия — всегда минус, допработы и перерасход — плюс: знак берётся из вида, человек вводит сумму
  const value = Number(amount.replace(/\s/g, '').replace(',', '.'));
  const signed =
    kind === 'saving' ? -Math.abs(value) : kind === 'transfer' || kind === 'other' ? value : Math.abs(value);
  const m = useMutation({
    mutationFn: () => {
      const body: BudgetChangeInput = {
        sectionLineId,
        kind,
        delta: signed,
        reason,
        [basisType]: basisId,
      };
      return api<BudgetChangesView>(`/projects/${projectId}/budget-changes`, {
        query: { versionId },
        body,
      });
    },
    onSuccess: (data) => {
      qc.setQueryData(budgetChangesQuery(projectId, versionId).queryKey, data);
      qc.invalidateQueries({ queryKey: ['inbox'] });
      toast('Изменение предложено: его утверждает другой человек');
      onClose();
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const ok = Number.isFinite(value) && value !== 0 && reason.trim().length >= 5 && sectionLineId && basisId;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Изменение бюджета"
      description="Утверждённый бюджет меняется только по документу: запросу на изменение, допсоглашению или договору (БМ-10)."
      footer={
        <>
          <Button className={BTN} variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className={BTN}
            variant="primary"
            loading={m.isPending}
            disabled={!ok}
            onClick={() => m.mutate()}
          >
            Предложить
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Вид">
          <Select value={kind} onChange={(e) => setKind(e.target.value as BudgetChangeKind)}>
            {(Object.keys(BUDGET_CHANGE_KINDS) as BudgetChangeKind[]).map((k) => (
              <option key={k} value={k}>
                {BUDGET_CHANGE_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Раздел бюджета">
          <Select value={sectionLineId} onChange={(e) => setSectionLineId(e.target.value)}>
            {sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={
            kind === 'transfer' || kind === 'other' ? 'Сумма, ₸ (минус — убрать из раздела)' : 'Сумма, ₸'
          }
          hint={
            kind === 'saving'
              ? 'Экономия уменьшает раздел'
              : kind === 'transfer'
                ? 'Для переноса заведите две записи с противоположными знаками'
                : 'Увеличивает раздел'
          }
        >
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Документ-основание">
          <Select
            value={basisType}
            onChange={(e) => {
              setBasisType(e.target.value as BasisType);
              setBasisId('');
            }}
          >
            {(Object.keys(BASIS_TITLES) as BasisType[]).map((k) => (
              <option key={k} value={k}>
                {BASIS_TITLES[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Какой именно" className="sm:col-span-2">
          <Select value={basisId} onChange={(e) => setBasisId(e.target.value)}>
            <option value="">
              {basis.isPending ? 'Загрузка…' : options.length ? 'Выберите' : 'Нет документов'}
            </option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.title}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Причина" className="sm:col-span-2">
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

export function BudgetChangesPanel({
  projectId,
  versionId,
  sections,
}: {
  projectId: string;
  versionId: string;
  sections: Array<{ id: string; name: string }>;
}) {
  const qc = useQueryClient();
  const q = useQuery(budgetChangesQuery(projectId, versionId));
  const [open, setOpen] = useState(false);
  const refresh = (data: BudgetChangesView) => {
    qc.setQueryData(budgetChangesQuery(projectId, versionId).queryKey, data);
    for (const k of ['budget', 'portfolio', 'inbox']) qc.invalidateQueries({ queryKey: [k] });
  };
  const decide = useMutation({
    mutationFn: (p: { id: string; approve: boolean; note: string | null }) =>
      api<BudgetChangesView>(`/budget-changes/${p.id}/decision`, {
        body: { approve: p.approve, note: p.note },
      }),
    onSuccess: refresh,
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const withdraw = useMutation({
    mutationFn: (id: string) => api<BudgetChangesView>(`/budget-changes/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const v = q.data;
  // Черновик правится напрямую: блок нужен утверждённому бюджету или уже сделанным записям
  if (!v.versionApproved && !v.items.length) return null;
  return (
    <Panel
      title="Изменения бюджета"
      meta={`БМ-10: утверждено ${tengeShort(v.approvedTotal)} — входит в бюджет разделов и маржу`}
      bodyClassName="p-0"
      actions={
        v.canPropose ? (
          <Button
            className={BTN}
            variant="primary"
            icon={<Plus className="size-4" />}
            onClick={() => setOpen(true)}
          >
            Изменение
          </Button>
        ) : null
      }
    >
      {v.items.length ? (
        <ul className="divide-y divide-line">
          {v.items.map((c) => (
            <li key={c.id} className="flex flex-col gap-1 px-5 py-2.5 text-[13px]">
              <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-4">
                <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                  <span className="font-medium">{c.sectionName}</span>{' '}
                  <Badge>{BUDGET_CHANGE_KINDS[c.kind]}</Badge>{' '}
                  <Badge tone={STATUS[c.status].tone}>{STATUS[c.status].title}</Badge>
                </span>
                <span className="num w-[130px] font-medium md:text-right">
                  {(c.delta ?? 0) > 0 ? '+' : ''}
                  {tengeShort(c.delta)}
                </span>
              </div>
              <p className="text-[12px] text-ink-2 [overflow-wrap:anywhere]">
                {c.reason}
                {c.basisNote ? ` · ${c.basisNote}` : ''}
              </p>
              <p className="text-[12px] [overflow-wrap:anywhere]">
                Основание:{' '}
                {c.basis.map((b, i) => (
                  <span key={b.id}>
                    {i ? ', ' : ''}
                    {b.path?.startsWith('/api/') ? (
                      <a className="text-accent-text underline-offset-2 hover:underline" href={b.path}>
                        {b.title}
                      </a>
                    ) : b.path ? (
                      <Link className="text-accent-text underline-offset-2 hover:underline" to={b.path}>
                        {b.title}
                      </Link>
                    ) : (
                      b.title
                    )}
                  </span>
                ))}
              </p>
              <p className="text-[12px] text-ink-2">
                {c.createdBy ?? '—'}
                {c.decidedBy ? ` → ${c.decidedBy}${c.decisionNote ? `: ${c.decisionNote}` : ''}` : ''}
              </p>
              {c.status === 'proposed' ? (
                <span className="flex flex-wrap gap-2">
                  {v.canDecide ? (
                    <>
                      <Button
                        className={BTN}
                        size="sm"
                        variant="primary"
                        loading={decide.isPending && decide.variables?.id === c.id}
                        onClick={() => decide.mutate({ id: c.id, approve: true, note: null })}
                      >
                        Утвердить
                      </Button>
                      <Button
                        className={BTN}
                        size="sm"
                        variant="ghost"
                        loading={decide.isPending && decide.variables?.id === c.id}
                        onClick={() => {
                          const note = window.prompt('Почему отклоняете?');
                          if (note?.trim()) decide.mutate({ id: c.id, approve: false, note });
                        }}
                      >
                        Отклонить
                      </Button>
                    </>
                  ) : null}
                  {v.canPropose ? (
                    <Button
                      className={BTN}
                      size="sm"
                      variant="ghost"
                      loading={withdraw.isPending}
                      onClick={() => withdraw.mutate(c.id)}
                    >
                      Отозвать
                    </Button>
                  ) : null}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-5 py-3 text-[13px] text-ink-2">
          Изменений нет. Допработы, экономия и перерасход записываются сюда со ссылкой на документ.
        </p>
      )}
      {open ? (
        <ProposeDialog
          projectId={projectId}
          versionId={versionId}
          sections={sections}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </Panel>
  );
}
